/**
 * What the footer and the legal reader fetch, and how they fail.
 *
 * Every endpoint here is already `@Public()` in core
 * (`core/admin/cms/cms-public.controller.ts`, `core/preview`), so the landing
 * page needs no backend change at all — it is the first UNAUTHENTICATED
 * consumer of routes that were built public from the start.
 *
 * ─── Everything degrades to nothing ─────────────────────────────────────────
 * Three separate things make these come back empty, and none of them is a
 * fault:
 *
 *   - A PREVIEW build answers `[]` for social links and 404 for every legal
 *     page, by design.
 *   - `GET /cms/legal/:slug` fails CLOSED and silently: an unseeded row and a
 *     typo'd slug are the same 404.
 *   - The page is reachable before the API is (a cold container, a static
 *     deploy ahead of the backend).
 *
 * So a fetch that fails resolves to an empty result rather than rejecting, and
 * the footer renders the whole block or omits it. A row of links that 404 is
 * worse than no row.
 *
 * ─── skipAuthRetry ──────────────────────────────────────────────────────────
 * `core/network/api.ts` answers a 401 by attempting a token refresh. On a page
 * whose entire audience is signed out that is a wasted round trip at best, and
 * at worst it churns auth state under a visitor who is about to sign in. These
 * routes are public and should never 401 — the flag makes sure that if one
 * ever does, it stops there.
 */
import { api } from '../../core/network/api';
import { getSiteInfo } from '../../core/preview/preview';
import { LEGAL_SLUGS } from './landing-copy';

export interface LegalPageSummary {
  slug: string;
  title: string;
}

export interface LegalPageDoc extends LegalPageSummary {
  /** Markdown. NEVER HTML — see `backend/prisma/content/legal-pages.ts`. */
  content: string;
  updatedAt?: string;
}

export interface SocialLink {
  platform: string;
  url: string;
}

/** One dispatch. `GET /cms/news` returns the summary; `:slug` adds `content`. */
export interface NewsItem {
  slug: string;
  title: string;
  summary: string;
  tag: string;
  imageUrl: string | null;
  publishedAt: string | null;
}

export interface NewsArticle extends NewsItem {
  /** Markdown, same rules as a legal page — admin-editable, never trusted as HTML. */
  content: string;
}

export interface NewsPage {
  items: NewsItem[];
  page: number;
  pageSize: number;
  total: number;
}

const PUBLIC = { skipAuthRetry: true } as const;

/**
 * The legal documents that actually exist right now, in the order
 * `LEGAL_SLUGS` declares.
 *
 * Fetched one per slug because that is the only shape core exposes — there is
 * no list endpoint for legal pages, only `:slug`. Three parallel requests on a
 * page that is already loading a WebGL scene, so they are fired together and
 * the whole thing is best-effort.
 */
export async function loadLegalIndex(): Promise<LegalPageSummary[]> {
  try {
    // `GET /api/legal` — every ACTIVE document, whatever an operator has added
    // in the admin panel. Janeks: *"should include everything that is
    // available in admin under Legal (more documents can be added)."*
    //
    // Game-side (`backend/src/game/account/legal-index.controller.ts`),
    // because core publishes no public listing route and core may not be
    // edited in a game repo. See that file.
    const rows = await api.get<LegalPageSummary[]>('/legal', PUBLIC);
    if (rows.length > 0) return rows.map(({ slug, title }) => ({ slug, title }));
  } catch { /* fall through */ }

  // The fallback probes the three slugs the seed writes, one request each. It
  // exists for a deployment running a backend older than that endpoint — the
  // frontend and the API ship as separate images, so "the route is not there
  // yet" is a real state. It cannot discover a fourth document, which is the
  // whole reason the endpoint above exists.
  const results = await Promise.all(
    LEGAL_SLUGS.map((slug) => loadLegalPage(slug).catch(() => null)),
  );
  return results
    .filter((p): p is LegalPageDoc => p !== null)
    .map(({ slug, title }) => ({ slug, title }));
}

/** One document. Rejects — the reader page needs to tell the difference between "missing" and "here". */
export function loadLegalPage(slug: string): Promise<LegalPageDoc> {
  return api.get<LegalPageDoc>(`/cms/legal/${encodeURIComponent(slug)}`, PUBLIC);
}

/**
 * The published dispatches, newest first.
 *
 * Resolves to an EMPTY page rather than rejecting. A preview build returns one
 * by design, a deployment that has published nothing returns one honestly, and
 * an unreachable API is not something a reader can act on — all three render
 * the same "nothing yet" state.
 */
export async function loadNews(page = 1): Promise<NewsPage> {
  try {
    return await api.get<NewsPage>(`/cms/news?page=${encodeURIComponent(String(page))}`, PUBLIC);
  } catch {
    return { items: [], page: 1, pageSize: 20, total: 0 };
  }
}

/** One dispatch. Rejects — the article page has to tell "missing" from "here". */
export function loadNewsArticle(slug: string): Promise<NewsArticle> {
  return api.get<NewsArticle>(`/cms/news/${encodeURIComponent(slug)}`, PUBLIC);
}

/**
 * Social links.
 *
 * Two sources carry these and they are not the same list: `/api/site-info`
 * embeds whatever the deployment configured, and `/api/cms/social-links` is
 * the admin-managed table. The CMS table is the one an operator edits from
 * `/admin`, so it wins; site-info is the fallback for a deployment that never
 * populated the table.
 */
export async function loadSocialLinks(): Promise<SocialLink[]> {
  try {
    const rows = await api.get<SocialLink[]>('/cms/social-links', PUBLIC);
    if (rows.length > 0) return rows.map(({ platform, url }) => ({ platform, url }));
  } catch { /* fall through to site-info */ }
  return getSiteInfo()?.socialLinks ?? [];
}
